<template>
  <b-modal :active.sync="isModalActive" :can-cancel="isUploading ? [] : ['x', 'escape', 'outside']" :width="1000">
    <div class="bg-white border rounded shadow-lg overflow-hidden">

      <header class="px-6 py-4 border-b bg-grey-lightest flex justify-between items-center">
        <h2 class="text-xl font-light uppercase text-blue-light tracking-wide">
          {{ $t('add_federated_ifc_model') }}
        </h2>
        <b-icon pack="fas" icon="layer-group" class="text-blue-light" />
      </header>

      <div class="p-0 flex flex-col md:flex-row">

        <div class="w-full md:w-5/12 p-6 border-r border-grey-lighter bg-white">

          <b-field :label="$t('federated_model_name')" class="mb-5">
            <b-input v-model="federationName" :placeholder="$t('federated_model_name')"
              :disabled="isUploading" icon-pack="fas" icon="tag" />
          </b-field>
          <b-button v-if="isExistingGroup && nameChanged" type="is-info" size="is-small"
            icon-pack="fas" icon-left="save" :loading="isSavingName" @click="saveName" class="mb-4">
            {{ $t('save') }}
          </b-button>

          <hr class="my-6 border-grey-lighter">

          <b-field>
            <b-upload v-model="selectedFiles" multiple accept=".ifc" drag-drop expanded :disabled="isUploading"
              @input="onFilesSelected">
              <section
                class="section p-4 border-2 border-dashed border-grey-light rounded-lg hover:bg-blue-lightest transition-colors cursor-pointer"
                :class="{ 'opacity-50': isUploading }">
                <div class="content has-text-centered text-grey">
                  <p>
                    <b-icon pack="fas" icon="cloud-upload-alt" size="is-large" />
                  </p>
                  <p class="text-sm font-medium">{{ $t("drop_files") }}</p>
                </div>
              </section>
            </b-upload>
          </b-field>
        </div>

        <div class="w-full md:w-7/12 p-6 bg-grey-lightest flex flex-col" style="max-height: 600px; overflow-y: auto;">

          <div v-if="pendingFiles.length > 0" class="mb-2">
            <div class="flex justify-between items-end mb-4 border-b pb-2">
              <div class="flex items-center">
                <h3 class="font-bold text-grey-dark">
                  {{ $t('files_to_upload') }} ({{ pendingFiles.length }})
                </h3>
                <a v-if="pendingFiles.length > 1" @click="pendingFiles = []"
                  class="ml-4 text-xs text-red hover:underline" :disabled="isUploading">
                  {{ $t('clean') }}
                </a>
              </div>

              <div class="text-right">
                <div v-if="!federationName" class="text-xs text-orange-dark mb-1 font-medium">
                  <b-icon pack="fas" icon="exclamation-circle" size="is-small" />
                  {{ $t('enter_name_to_upload') }}
                </div>
                <b-button type="is-primary" icon-pack="fas" icon="upload" :loading="isUploading"
                  :disabled="!federationName" @click="uploadAll">
                  {{ $t('upload_all') }}
                </b-button>
              </div>
            </div>

            <div v-for="(f, i) in pendingFiles" :key="'pending-' + i"
              class="mb-3 p-3 bg-white border rounded shadow-sm hover:shadow-md transition-shadow">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center overflow-hidden">
                  <b-icon pack="fas" icon="file-contract" class="text-blue mr-2" size="is-small" />
                  <span class="text-xs font-mono text-grey-dark truncate italic" :title="f.file.name">
                    {{ f.file.name }}
                  </span>
                  <span class="ml-2 tag is-light is-small text-xs italic">{{ formatSize(f.file.size) }}</span>
                </div>
                <button class="button is-small is-white text-red" @click="removePending(i)" :disabled="isUploading">
                  <b-icon pack="fas" icon="trash-alt" size="is-small" />
                </button>
              </div>

              <b-field :label="$t('display_name')" custom-class="text-xs mb-1 text-grey-dark">
                <b-input v-model="f.displayName" size="is-small" :placeholder="$t('display_name')" expanded />
              </b-field>
            </div>
          </div>

          <div v-if="uploadQueue.length > 0" class="mt-4">
            <h3 class="font-bold text-grey-dark mb-3 border-b pb-2 italic uppercase text-xs tracking-widest">
              {{ $t('uploading') }}
            </h3>
            <div v-for="item in uploadQueue" :key="item.name" class="mb-4 bg-white p-3 rounded border shadow-sm">
              <div class="flex justify-between items-center mb-2 text-sm">
                <div class="flex items-center truncate mr-2">
                  <b-icon pack="fas" :icon="statusIcon(item.status)" :class="statusClass(item.status)" size="is-small"
                    :custom-class="item.status === 'uploading' ? 'fa-spin' : ''" />
                  <span class="ml-2 font-medium truncate" :class="item.status === 'error' ? 'text-red' : ''"
                    :title="item.name">
                    {{ item.name }}
                  </span>
                </div>
                <span class="text-xs font-bold w-12 text-right text-grey-dark" v-if="item.status === 'uploading'">
                  {{ item.progress }}%
                </span>
              </div>
              <b-progress v-if="item.status === 'uploading' || item.status === 'pending'" :value="item.progress"
                type="is-primary" size="is-small" class="mt-1"></b-progress>
            </div>
          </div>

          <div v-if="addedFiles.length > 0"
            class="mt-auto border-t pt-4 text-xs font-semibold uppercase text-grey-dark italic">
            <div class="flex items-center justify-between mb-1">
              <span>{{ $t('ifc_models_in_group') }}</span>
              <span class="text-grey normal-case font-normal text-xxs">
                <b-icon v-if="isSavingOrder" pack="fas" icon="spinner" custom-class="fa-spin" size="is-small" />
                <template v-else>
                  <b-icon pack="fas" icon="grip-vertical" size="is-small" />
                  {{ $t('ifc_models_drag_to_reorder') }}
                </template>
              </span>
            </div>
            <draggable
              v-model="addedFiles"
              :animation="150"
              handle=".drag-handle"
              ghost-class="opacity-25"
              :disabled="isUploading || isSavingOrder"
              @end="onReorderEnd"
            >
              <div v-for="(item, i) in addedFiles" :key="'added-' + (item.id || i)"
                class="flex items-center justify-between mt-2 py-2 px-3 bg-green-lightest text-green-dark border border-green-lighter rounded shadow-sm">
                <div class="flex items-center min-w-0">
                  <span class="drag-handle mr-2 cursor-move text-grey-dark hover:text-grey-darker"
                    :title="$t('ifc_models_drag_to_reorder')">
                    <b-icon pack="fas" icon="grip-vertical" size="is-small" />
                  </span>
                  <span class="text-grey-dark text-xxs font-mono mr-2 w-6 text-right">{{ i + 1 }}.</span>
                  <b-icon pack="fas" :icon="item.done ? 'check-circle' : 'history'" size="is-small" class="mr-2" />
                  <span class="truncate" :title="item.name">{{ item.name }}</span>
                </div>
                <button v-if="item.id" @click="removeExistingDoc(item, i)"
                  class="ml-2 flex-shrink-0 text-red hover:text-red-dark" :disabled="isUploading">
                  <b-icon pack="fas" icon="trash-alt" size="is-small" />
                </button>
              </div>
            </draggable>
          </div>

          <div v-if="pendingFiles.length === 0 && uploadQueue.length === 0 && addedFiles.length === 0"
            class="flex flex-col items-center justify-center h-full text-grey-light py-20">
            <b-icon pack="fas" icon="folder-open" size="is-large" class="mb-4 opacity-50" />
            <p class="italic uppercase tracking-widest text-xs opacity-75">{{ $t('no_files_selected') }}</p>
          </div>
        </div>
      </div>

      <footer class="p-4 bg-white border-t flex justify-end">
        <b-button @click="hide" type="is-light"
          class="px-8 font-semibold uppercase text-xs tracking-widest hover:bg-grey-lighter transition-colors"
          :disabled="isUploading">
          {{ isUploading ? $t('uploading')+'....' : $t('close') }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from "axios";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import { mapGetters } from "vuex";
import store from "~/store";
import draggable from "vuedraggable";

export default {
  name: "upload-ifc-federated",

  components: { draggable },

  data: () => ({
    isModalActive: false,
    isUploading: false,
    isSavingName: false,
    isSavingOrder: false,
    isExistingGroup: false,
    originalName: "",
    federationId: null,
    federationName: "",
    selectedFiles: [],
    pendingFiles: [],
    uploadQueue: [],
    addedFiles: [],
    uppy: null,
  }),

  computed: {
    ...mapGetters({ project: "app/project" }),
    nameChanged() {
      return this.federationName !== this.originalName;
    },
  },

  created() {
    this._buildUppy();
  },

  beforeDestroy() {
    if (this.uppy) this.uppy.destroy();
  },

  methods: {
    show() {
      this.isExistingGroup = false;
      this.federationId = crypto.randomUUID();
      this.federationName = "";
      this.originalName = "";
      this.addedFiles = [];
      this.pendingFiles = [];
      this.selectedFiles = [];
      this.uploadQueue = [];
      this.isUploading = false;
      this.isModalActive = true;
    },

    showForGroup(federationId, federationName, existingDocs) {
      this.isExistingGroup = true;
      this.federationId = federationId;
      this.federationName = federationName;
      this.originalName = federationName;
      this.addedFiles = (existingDocs || []).map(d => ({ id: d.id, name: d.name, done: true }));
      this.pendingFiles = [];
      this.selectedFiles = [];
      this.uploadQueue = [];
      this.isUploading = false;
      this.isModalActive = true;
    },

    hide() {
      this.$emit("success");
      this.isModalActive = false;
    },

    // Llamado por vuedraggable al soltar un item. Persiste el nuevo orden
    // en el backend mandando los IDs en su nueva secuencia. Si la llamada
    // falla, el front se queda con el orden visual nuevo y el siguiente
    async onReorderEnd() {
      // Solo persistimos si hay items ya creados en backend (con id).
      const ids = this.addedFiles.filter(f => f.id).map(f => f.id);
      if (ids.length < 2 || !this.isExistingGroup) return;
      this.isSavingOrder = true;
      try {
        await axios.post(
          `/api/v2/project/${this.project.id}/ifc-federated-document/${this.federationId}/reorder`,
          { ifc_ids: ids }
        );
        this.$buefy.toast.open({
          message: this.$t('ifc_models_order_saved'),
          type: 'is-success',
          position: 'is-bottom-right',
          duration: 2000,
        });
      } catch (e) {
        this.$buefy.toast.open({
          message: this.$t('error_saving'),
          type: 'is-danger',
          position: 'is-bottom-right',
        });
      } finally {
        this.isSavingOrder = false;
      }
    },

    async saveName() {
      if (!this.federationName || !this.nameChanged) return;
      this.isSavingName = true;
      try {
        await axios.post(`/api/v2/project/${this.project.id}/ifc-federated-document/${this.federationId}/rename`, {
          federation_name: this.federationName
        });
        this.originalName = this.federationName;
        this.$buefy.toast.open({ message: this.$t('success_saving_document'), type: 'is-success', position: 'is-bottom-right' });
      } catch (e) {
        this.$buefy.toast.open({ message: this.$t('error_saving'), type: 'is-danger', position: 'is-bottom-right' });
      } finally {
        this.isSavingName = false;
      }
    },

    async removeExistingDoc(item, index) {
      this.$dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_generic', [item.name]),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          try {
            await axios.post(`/api/v2/ifc-document/${item.id}/delete`);
            this.addedFiles.splice(index, 1);
            this.$buefy.toast.open({ message: this.$t('success_deleting_generic'), type: 'is-success', position: 'is-bottom-right' });
          } catch (e) {
            this.$buefy.toast.open({ message: this.$t('error_saving'), type: 'is-danger', position: 'is-bottom-right' });
          }
        }
      });
    },

    onFilesSelected(files) {
      if (!files) return;
      const arr = Array.isArray(files) ? files : [files];

      arr.forEach((f) => {
        if (f && !this.pendingFiles.some((p) => p.file.name === f.name)) {

          // SMART NAMING: Limpieza de nombres técnicos BIM
          let name = f.name.replace(/\.[^/.]+$/, '');
          name = name.replace(/_/g, ' ').trim();

          if (name.length > 25) {
            const segments = name.includes('-') ? name.split('-') : name.split(' ');
            if (segments.length > 2) {
              name = segments.slice(-2).join(' - ').trim();
            }
          }

          this.pendingFiles.push({
            file: f,
            displayName: name
          });
        }
      });
      // Limpiamos el v-model para permitir volver a seleccionar el mismo archivo si se borró
      this.$nextTick(() => { this.selectedFiles = []; });
    },

    removePending(index) {
      this.pendingFiles.splice(index, 1);
    },

    _buildUppy() {
      if (this.uppy) { try { this.uppy.destroy(); } catch (e) { } }
      this.uppy = new Uppy({
        autoProceed: false,
        allowMultipleUploadBatches: false,
      }).use(AwsS3, {
        shouldUseMultipart: true,
        limit: 1,
        companionUrl: "/api/v2/",
        endpoint: "/api/v2/",
        headers: { Authorization: "Bearer " + store.getters["auth/token"] },
      });
    },

    async uploadAll() {
      if (!this.pendingFiles.length || !this.federationName || this.isUploading) return;
      this.isUploading = true;

      this.uploadQueue = this.pendingFiles.map((f) => ({
        name: f.displayName || f.file.name,
        file: f.file,
        status: "pending",
        progress: 0,
      }));
      this.pendingFiles = [];

      for (const item of this.uploadQueue) {
        await this._uploadOne(item);
      }

      this.isUploading = false;

      const errors = this.uploadQueue.filter(i => i.status === 'error');

      if (errors.length === 0) {
        this.$buefy.toast.open({
          message: this.$t('models_uploaded_successfully'),
          type: 'is-success',
          position: 'is-bottom-right'
        });
        setTimeout(() => { this.uploadQueue = []; }, 1500);
      } else {
        this.$buefy.toast.open({
          message: this.$t('upload_errors_occurred', { count: errors.length }),
          type: 'is-danger',
          position: 'is-bottom-right',
          duration: 5000
        });
        this.uploadQueue = errors;
      }
    },

    async _uploadOne(item) {
      item.status = "uploading";
      this._buildUppy();

      this.uppy.on("upload-progress", (file, progress) => {
        item.progress = Math.round((progress.bytesUploaded * 100) / progress.bytesTotal);
      });

      try {
        const fileName = item.file?.name || "";
        const extMatch = fileName.match(/(\.[^./\\]+)$/);
        const ext = extMatch ? extMatch[1] : ".ifc";
        const nameWithExt = `${item.name || fileName}${ext}`; // añade extension
        this.uppy.addFile({
          name: nameWithExt,
          type: item.file.type || "application/octet-stream",
          data: item.file,
          source: "Local",
        });

        const result = await this.uppy.upload();

        if (!result.successful || result.successful.length === 0) {
          item.status = "error";
          return;
        }

        const key = result.successful[0].response?.body?.key;
        if (!key) { item.status = "error"; return; }

        const resp = await axios.post(
          `/api/v2/project/${this.project.id}/ifc-federated-document`,
          {
            ifcpath: key,
            name: nameWithExt, // con extension igual que upload-ifc-document.vue
            federation_id: this.federationId,
            federation_name: this.federationName,
          }
        );

        if (resp?.data?.success) {
          item.status = "done";
          item.progress = 100;
          this.addedFiles.push({ name: item.name, done: false });
        } else {
          item.status = "error";
        }
      } catch (e) {
        console.error("[UploadIfcFederated]", item.name, e);
        item.status = "error";
      }
    },

    statusIcon(status) {
      return {
        pending: "clock",
        uploading: "spinner",
        done: "check-circle",
        error: "exclamation-circle"
      }[status] || "clock";
    },

    statusClass(status) {
      return {
        pending: "text-grey",
        uploading: "text-blue",
        done: "text-green",
        error: "text-red"
      }[status] || "";
    },

    formatSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
  },
};
</script>

<style scoped>
.bg-grey-lightest {
  background-color: #f9fafb;
}

.text-blue-light {
  color: #3273dc;
}

.border-blue-light {
  border-color: #3273dc;
}

.bg-blue-lightest {
  background-color: #eef3fc;
}

.bg-orange-lightest {
  background-color: #fffaf0;
}

.text-orange-dark {
  color: #c05621;
}

.border-orange-lighter {
  border-color: #fbd38d;
}

.bg-green-lightest {
  background-color: #f0fff4;
}

.border-green-lighter {
  border-color: #c6f6d5;
}

.text-green-dark {
  color: #22543d;
}
</style>